import React, { useMemo } from 'react';
import { Table, TableProps, Typography } from 'antd';
import { GetCallDetailsDto } from 'types';

interface DashboardTableProps {
    callStatsBySource?: GetCallDetailsDto[];
}

// Status-based interface (transposed structure)
interface CallStatByStatus {
    status: string;
    isPercentageField?: boolean;
    percentageValue?: number;
    [source: string]: string | number | boolean | undefined;
}

// Mapping from field names to display names for call stats
const fieldToDisplayName = {
    'incomingCalls': 'Đang gọi vào',
    'waitingForAgent': 'Đang chờ gặp ĐTV',
    'isTalking': 'Đang đàm thoại',
    'agentReceive': 'ĐTV đã tiếp nhận',
    'botReceive': 'Bot đã tiếp nhận',
    'missedCall': 'Gọi nhỡ',
    'totalCall': 'Đã gọi đến tổng đài',
};

// Mapping for source display names
const sourceDisplayNames: Record<string, string> = {
    'telco': 'VNPT',
    'webcall': 'WEBCALL',
    'appcall': 'APPCALL',
    'audiocall': 'VOICE',
    'videocall': 'VIDEO',
};

const allExpectedSources = ['telco', 'webcall', 'appcall', 'audiocall', 'videocall'];

// Function to get display name for a source
const getSourceDisplayName = (source: string): string => {
    return sourceDisplayNames[source.toLowerCase()] || source.toUpperCase();
};

// Add a formatting function for percentages
const formatPercentage = (value: number): string => {
    if (value === 0) return "0%";
    return `${value.toFixed(2)}%`;
};

// Function to transform data from source-based to status-based
const transformCallStatsBySource = (sourceBasedData: GetCallDetailsDto[]): CallStatByStatus[] => {
    const fields = [
        'incomingCalls',
        'waitingForAgent',
        'isTalking',
        'agentReceive',
        'botReceive',
        // 'missedCall',
        // 'totalCall',
    ];

    // Get all sources from the data
    const actualSources = sourceBasedData.map(item => item.callSource);

    // Combine actual sources with expected sources
    const allSources = Array.from(new Set([...actualSources, ...allExpectedSources]));

    // Calculate percentage for missed calls
    let totalMissedCalls = 0;
    let grandTotalCalls = 0;

    sourceBasedData.forEach(sourceData => {
        if (sourceData.missedCall !== undefined && sourceData.missedCall !== null) {
            totalMissedCalls += sourceData.missedCall;
        }
        if (sourceData.totalCall !== undefined && sourceData.totalCall !== null) {
            grandTotalCalls += sourceData.totalCall;
        }
    });

    // Transform data structure
    return fields.map(field => {
        // Create a row with the display name as status
        const row: CallStatByStatus = {
            status: fieldToDisplayName[field as keyof typeof fieldToDisplayName] || field,
            isPercentageField: field === 'missedCall',
            percentageValue: field === 'missedCall' && grandTotalCalls > 0
                ? (totalMissedCalls / grandTotalCalls) * 100
                : undefined
        };

        // Initialize all sources with 0
        let rowTotal = 0;
        allSources.forEach(source => {
            if (source) {
                row[source] = 0;
            }
        });

        // Add values from actual data
        sourceBasedData.forEach(sourceData => {
            const value = sourceData[field as keyof GetCallDetailsDto];
            if (value !== undefined && value !== null && sourceData.callSource) {
                row[sourceData.callSource] = value;
                rowTotal += Number(value);
            }
        });

        // Add total column
        row['total'] = rowTotal;
        return row;
    });
};

const DashboardTable: React.FC<DashboardTableProps> = ({ callStatsBySource = [] }) => {
    // Transform from source-based to status-based structure
    const callStatsByStatus = useMemo(() =>
        transformCallStatsBySource(callStatsBySource),
        [callStatsBySource]);

    // Generate dynamic columns
    const getCallStatColumns = (): TableProps<CallStatByStatus>['columns'] => {
        // Use all expected sources
        const allSources = Array.from(new Set([
            ...(callStatsBySource ? callStatsBySource.map(item => item.callSource) : []),
            ...allExpectedSources
        ]));

        // Base column for status
        const columns: TableProps<CallStatByStatus>['columns'] = [
            {
                title: 'Trạng thái',
                dataIndex: 'status',
                key: 'status',
                className: 'text-left',
                render: (text: string) =>
                    text === 'Gọi nhỡ' ?
                        <span className="text-red-500 font-bold">{text}</span> :
                        text
            }
        ];

        // Add a column for each source
        allSources.forEach(source => {
            if (source) {
                columns.push({
                    title: getSourceDisplayName(source),
                    dataIndex: source,
                    key: source,
                    className: 'text-center',
                    render: (value: any, record) => {
                        const displayValue = typeof value === 'object' ?
                            JSON.stringify(value) :
                            value === undefined || value === null ? 0 : value;

                        // Show percentage for missed calls
                        if (record.isPercentageField && source !== 'total') {
                            const sourceTotal = callStatsBySource?.find(item =>
                                item.callSource === source)?.totalCall || 0;
                            const percentage = sourceTotal > 0 ?
                                ((displayValue as number) / sourceTotal) * 100 : 0;

                            return (
                                <span className="text-red-500 font-bold">
                                    {displayValue}
                                    <span className="text-xs ml-1 text-red-400">
                                        ({formatPercentage(percentage)})
                                    </span>
                                </span>
                            );
                        }

                        return record.status === 'Gọi nhỡ' ?
                            <span className="text-red-500 font-bold">{displayValue}</span> :
                            displayValue;
                    }
                });
            }
        });

        // Add Total column
        columns.push({
            title: 'Tổng',
            dataIndex: 'total',
            key: 'total',
            className: 'font-bold text-center',
            render: (value: any, record) => {
                const displayValue = typeof value === 'object' ?
                    JSON.stringify(value) :
                    value === undefined || value === null ? 0 : value;

                // Show percentage for missed calls total
                if (record.isPercentageField && record.percentageValue !== undefined) {
                    return (
                        <span className="text-red-500 font-bold">
                            {displayValue}
                            <span className="text-xs ml-1 text-red-400">
                                ({formatPercentage(record.percentageValue)})
                            </span>
                        </span>
                    );
                }

                return record.status === 'Gọi nhỡ' ?
                    <span className="text-red-500 font-bold">{displayValue}</span> :
                    <span className="font-semibold">{displayValue}</span>;
            }
        });

        return columns;
    };

    const callStatStatusColumns = useMemo(() => getCallStatColumns(), [callStatsBySource]);

    return (
        <Table<CallStatByStatus>
            columns={callStatStatusColumns}
            dataSource={callStatsByStatus}
            pagination={false}
            rowKey="status"
            size="small"
            className="no-borders text-center"
            bordered={false}
            onRow={() => ({
                style: { borderBottom: 'none' }
            })}
            components={{
                header: {
                    cell: (props: React.HTMLProps<HTMLTableCellElement>) =>
                        <th {...props} className="text-center" style={{ textAlign: 'center' }} />
                },
                body: {
                    row: (props: React.HTMLProps<HTMLTableRowElement>) =>
                        <tr {...props} style={{ border: 'none', borderBottom: 'none' }} />,
                    cell: (props: React.HTMLProps<HTMLTableCellElement>) =>
                        <td {...props} style={{ border: 'none', borderBottom: 'none', textAlign: 'center' }} />
                }
            }}
        />
    );
};

export default React.memo(DashboardTable);
